/**
 * Answer depth — how much research the user wants behind the next answer.
 *
 * The backend owns the vocabulary (`maf/executors/router_executor.py`) and
 * normalizes anything it does not recognize back to `auto`, so an older client
 * or a stale value degrades to the router's judgment rather than pinning the
 * voter to one end of the range.
 *
 * - `auto`  — the router decides, and says so when it escalates to deep.
 * - `quick` — an explicit refusal to wait.
 * - `deep`  — explicit consent to wait for a multi-source answer.
 */
export const ANSWER_DEPTHS = ['auto', 'quick', 'deep'] as const;

export type AnswerDepth = (typeof ANSWER_DEPTHS)[number];

export const DEFAULT_ANSWER_DEPTH: AnswerDepth = 'auto';

/** i18n keys for each option's label and one-line explanation. */
export const depthLabelKey = (depth: AnswerDepth) => `input.depth.options.${depth}.label`;
export const depthHintKey = (depth: AnswerDepth) => `input.depth.options.${depth}.hint`;

/**
 * The value to put on the request body, or `undefined` to omit the field.
 *
 * `auto` is the server's own default, so omitting it keeps the request byte
 * identical to what every client sent before this control existed — the depth
 * field only appears once a voter has actually asked for something else.
 */
export function depthForRequest(depth: AnswerDepth): AnswerDepth | undefined {
  return depth === DEFAULT_ANSWER_DEPTH ? undefined : depth;
}

/**
 * Which research agent actually answered, as the backend reports it on the
 * `end` frame. Separate from `AnswerDepth` because `auto` is a request, not an
 * outcome — `standard` and `deep` are what it resolves to, and `quick` arrives
 * only when the voter asked for it by name.
 */
export const RESEARCH_DEPTHS = ['standard', 'deep', 'quick'] as const;

export type ResearchDepth = (typeof RESEARCH_DEPTHS)[number];

/** Narrow an arbitrary stream value onto a known depth, or `undefined`. */
export const asAnswerDepth = (value: unknown): AnswerDepth | undefined =>
  ANSWER_DEPTHS.find((d) => d === value);

export const asResearchDepth = (value: unknown): ResearchDepth | undefined =>
  RESEARCH_DEPTHS.find((d) => d === value);

/**
 * The i18n key naming a depth, shared by the badge on a sent message and the
 * tag beside "How I researched this" — the same turn, so the same words.
 *
 * Whole strings per case rather than a template around the option labels: the
 * Spanish labels agree with "respuesta" (feminine) and would misagree inside a
 * sentence built around "modo".
 */
export const depthTagKeys = {
  deep: 'depthTag.deep',
  quick: 'depthTag.quick',
} as const;

/**
 * The tag for an answered turn, or `null` when there is nothing to report.
 *
 * The tag marks the turns the voter steered — the ones where they chose Fast or
 * Detailed and the answer honored it. A turn left on Normal carries no tag:
 * naming the default on every answer was noise beside the disclosure it sits
 * next to, and the composer's own setting already says how the next answer will
 * be researched.
 *
 * `research` is absent on turns no research agent handled (the transparency and
 * ballot-builder paths) and on conversations that predate the field, which is
 * one more reason to show no tag at all.
 */
export function depthTagKey(
  answer: AnswerDepth | undefined,
  research: ResearchDepth | undefined
): string | null {
  if (!research) return null;
  // Both halves have to agree before the tag speaks. `_pick_target` sends every
  // `deep` request to the deep agent and only an explicit `quick` request ever
  // reaches the quick agent, so a mismatched pair cannot arise from the server
  // — and if it ever does, saying nothing beats misreporting the turn.
  if (answer === 'deep') return research === 'deep' ? depthTagKeys.deep : null;
  // `standard` alongside a `quick` request is what an older backend reports,
  // since `quick` ran the standard agent before it had its own. The voter still
  // asked for fast, so the tag still says so.
  if (answer === 'quick')
    return research === 'quick' || research === 'standard' ? depthTagKeys.quick : null;
  return null;
}
